import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState(() => {
    const savedCart = localStorage.getItem('cart');
    return savedCart ? JSON.parse(savedCart) : [];
  });
  const [sideCartOpen, setSideCartOpen] = useState(false);
  const [allProducts, setAllProducts] = useState([]);
  const [freeShippingThreshold, setFreeShippingThreshold] = useState(450);
  const [freeShippingPopup, setFreeShippingPopup] = useState(false);
  const [lostFreeShippingPopup, setLostFreeShippingPopup] = useState(false);

  useEffect(() => {
    localStorage.setItem('cart', JSON.stringify(cart));
  }, [cart]);

  // Sync cart prices with server on load + fetch all products
  useEffect(() => {
    const syncPrices = async () => {
      try {
        const resp = await axios.get(`${API}/products`);
        const products = resp.data;
        setAllProducts(products);
        if (cart.length === 0) return;
        const priceMap = {};
        products.forEach(p => { priceMap[p.id] = p.price; });
        setCart(prev => prev.map(item => {
          const serverPrice = priceMap[item.id];
          if (serverPrice && serverPrice !== item.price) {
            return { ...item, price: serverPrice };
          }
          return item;
        }));
      } catch (e) { /* silent */ }
    };
    syncPrices();
    // Fetch free shipping threshold from banner
    const fetchThreshold = async () => {
      try {
        const r = await axios.get(`${API}/banner`);
        if (r.data.free_shipping_threshold) setFreeShippingThreshold(r.data.free_shipping_threshold);
      } catch (e) {}
    };
    fetchThreshold();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const addToCart = (product, quantity = 1) => {
    setCart((prevCart) => {
      const existingItem = prevCart.find((item) => item.id === product.id);
      let newCart;
      if (existingItem) {
        newCart = prevCart.map((item) =>
          item.id === product.id
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      } else {
        newCart = [...prevCart, { ...product, quantity }];
      }

      // Check free shipping threshold crossing
      if (freeShippingThreshold > 0) {
        const oldTotal = prevCart.reduce((sum, item) => sum + item.price * item.quantity, 0);
        const newTotal = newCart.reduce((sum, item) => sum + item.price * item.quantity, 0);
        if (oldTotal < freeShippingThreshold && newTotal >= freeShippingThreshold) {
          setLostFreeShippingPopup(false);
          setFreeShippingPopup(true);
          setTimeout(() => setFreeShippingPopup(false), 5000);
        }
      }

      return newCart;
    });
    setSideCartOpen(true);
  };

  const checkLostFreeShipping = (oldCart, newCart) => {
    if (freeShippingThreshold > 0) {
      const oldTotal = oldCart.reduce((sum, item) => sum + item.price * item.quantity, 0);
      const newTotal = newCart.reduce((sum, item) => sum + item.price * item.quantity, 0);
      if (oldTotal >= freeShippingThreshold && newTotal < freeShippingThreshold) {
        setFreeShippingPopup(false);
        setLostFreeShippingPopup(true);
        setTimeout(() => setLostFreeShippingPopup(false), 5000);
      }
    }
  };

  const removeFromCart = (productId) => {
    setCart((prevCart) => {
      const newCart = prevCart.filter((item) => item.id !== productId);
      checkLostFreeShipping(prevCart, newCart);
      return newCart;
    });
  };

  const updateQuantity = (productId, quantity) => {
    if (quantity <= 0) {
      removeFromCart(productId);
      return;
    }
    
    setCart((prevCart) => {
      const newCart = prevCart.map((item) =>
        item.id === productId ? { ...item, quantity } : item
      );
      checkLostFreeShipping(prevCart, newCart);
      // Also check if crossing threshold upward
      if (freeShippingThreshold > 0) {
        const oldTotal = prevCart.reduce((sum, item) => sum + item.price * item.quantity, 0);
        const newTotal = newCart.reduce((sum, item) => sum + item.price * item.quantity, 0);
        if (oldTotal < freeShippingThreshold && newTotal >= freeShippingThreshold) {
          setLostFreeShippingPopup(false);
          setFreeShippingPopup(true);
          setTimeout(() => setFreeShippingPopup(false), 5000);
        }
      }
      return newCart;
    });
  };

  const clearCart = () => {
    setCart([]);
  };

  const getCartTotal = () => {
    return cart.reduce((total, item) => total + item.price * item.quantity, 0);
  };

  const getCartCount = () => {
    return cart.reduce((count, item) => count + item.quantity, 0);
  };

  return (
    <CartContext.Provider
      value={{
        cart,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        getCartTotal,
        getCartCount,
        sideCartOpen,
        setSideCartOpen,
        allProducts,
        freeShippingThreshold,
        freeShippingPopup,
        setFreeShippingPopup,
        lostFreeShippingPopup,
        setLostFreeShippingPopup,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};